/**
 * Builds a compact summary of locally stored TENSION data for Atlas (Groq) coaching context.
 * Data stays in the user's browser until sent with their chat request.
 */
import {
  buildCoupleOperatingSystemScore,
  buildRelationshipIntelligenceModel,
  getCounterfactualCalibration,
  readCounterfactualOutcomes,
} from './relationship-intelligence-engine';

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function num(x: unknown): number | null {
  const n = typeof x === 'number' ? x : Number(x);
  return Number.isFinite(n) ? n : null;
}

export function buildTensionUserSnapshotForAtlas(): Record<string, unknown> {
  if (typeof localStorage === 'undefined') {
    return { source: 'none', reason: 'no_localStorage' };
  }

  const mode = localStorage.getItem('tension_mode') || 'unknown';
  const activeRelationshipId = localStorage.getItem('tension_active_relationship') || '';
  const relationships = safeParse<unknown[]>(localStorage.getItem('tension_relationships'), []);
  const rel = Array.isArray(relationships)
    ? relationships.find((r: any) => r && String(r.id) === activeRelationshipId)
    : null;
  const relationshipLabel =
    rel && typeof (rel as any).name === 'string' ? (rel as any).name : activeRelationshipId || 'active';

  const profile = safeParse<Record<string, unknown>>(localStorage.getItem('tension_user_profile'), {});
  const attachmentStyle =
    typeof profile.attachmentStyle === 'string' ? profile.attachmentStyle : undefined;

  const allCheckins = safeParse<any[]>(localStorage.getItem('tension_checkins'), []);
  const list = Array.isArray(allCheckins) ? allCheckins : [];

  const forRel = activeRelationshipId
    ? list.filter((c: any) => String(c?.relationshipId || '') === activeRelationshipId)
    : list;

  const sorted = [...forRel].sort((a, b) => {
    const ta = new Date(a?.timestamp || a?.date || 0).getTime();
    const tb = new Date(b?.timestamp || b?.date || 0).getTime();
    return ta - tb;
  });

  const recent = sorted.slice(-24);
  const uniqueActiveDayKeys = new Set(
    forRel
      .map((c) => {
        const raw = c?.timestamp || c?.date;
        if (!raw) return '';
        const ms = new Date(raw).getTime();
        if (!Number.isFinite(ms)) return '';
        return new Date(ms).toISOString().slice(0, 10);
      })
      .filter(Boolean),
  );

  const scores = recent.map((c) => num(c?.rtiScore)).filter((n): n is number => n != null);
  const avg = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
  const lastScore = scores.length ? scores[scores.length - 1] : null;
  const firstScore = scores.length ? scores[0] : null;

  let trend: 'up' | 'down' | 'stable' | 'unknown' = 'unknown';
  if (scores.length >= 4) {
    const half = Math.floor(scores.length / 2);
    const a = scores.slice(0, half);
    const b = scores.slice(half);
    const ma = a.reduce((x, y) => x + y, 0) / a.length;
    const mb = b.reduce((x, y) => x + y, 0) / b.length;
    const d = mb - ma;
    if (d > 4) trend = 'up';
    else if (d < -4) trend = 'down';
    else trend = 'stable';
  }

  const triggerCounts: Record<string, number> = {};
  for (const c of recent) {
    const tr = c?.triggers;
    if (Array.isArray(tr)) {
      for (const t of tr) {
        const s = String(t).trim();
        if (s) triggerCounts[s] = (triggerCounts[s] || 0) + 1;
      }
    }
  }
  const topTriggers = Object.entries(triggerCounts)
    .sort((x, y) => y[1] - x[1])
    .slice(0, 8)
    .map(([name, count]) => ({ name, count }));

  const componentKeys = [
    'initiation',
    'latency',
    'emotionalDensity',
    'availability',
    'withdrawal',
  ];
  const compAvg: Record<string, number> = {};
  for (const k of componentKeys) {
    const vals = recent
      .map((c) => num(c?.components?.[k]))
      .filter((n): n is number => n != null);
    if (vals.length) {
      compAvg[k] = Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
    }
  }

  const timelineSample = recent.slice(-8).map((c) => ({
    date: c?.date || c?.timestamp || '',
    rtiScore: num(c?.rtiScore),
    band: typeof c?.rtiBand === 'string' ? c.rtiBand : undefined,
    triggers: Array.isArray(c?.triggers) ? c.triggers.slice(0, 5) : [],
  }));

  const relationshipIntelligence = buildRelationshipIntelligenceModel(forRel);
  const osScore = buildCoupleOperatingSystemScore(forRel, relationshipIntelligence);
  const calibration = getCounterfactualCalibration();
  const outcomeHistory = readCounterfactualOutcomes().slice(-12);

  return {
    source: 'client_local_summary',
    generatedAt: new Date().toISOString(),
    mode,
    relationship: { id: activeRelationshipId || null, label: relationshipLabel },
    profileHints: {
      attachmentStyle: attachmentStyle || null,
    },
    checkIns: {
      countInAppForRelationship: forRel.length,
      activeDaysInAppForRelationship: uniqueActiveDayKeys.size,
      countInSample: recent.length,
      lastScore,
      averageScoreInSample: avg != null ? Math.round(avg * 10) / 10 : null,
      scoreTrendInSample: trend,
      firstScoreInSample: firstScore,
    },
    recurringTriggers: topTriggers,
    componentAveragesInSample: Object.keys(compAvg).length ? compAvg : null,
    recentCheckInsSample: timelineSample,
    relationshipIntelligence: {
      weatherBand: relationshipIntelligence.weatherBand,
      direction: relationshipIntelligence.direction,
      volatility: relationshipIntelligence.volatility,
      dataQuality: relationshipIntelligence.dataQuality,
      topSignals: relationshipIntelligence.topSignals,
      interventionWindows: relationshipIntelligence.interventionWindows,
      summary: relationshipIntelligence.summary,
      coupleOperatingSystemScore: osScore,
      counterfactualCalibration: calibration,
      recentOutcomeLoop: outcomeHistory.map((o) => ({
        at: o.at,
        style: o.style,
        predictedDelta: o.predictedDelta,
        actualDelta: o.actualDelta,
      })),
    },
  };
}
